import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Themes from '../../../components/Themes';
import translations from '../../../translations';

const DownloadInvites = () => {
  const { id } = useParams();
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');
  const t = translations[language];
  const [galleryImages, setGalleryImages] = useState([]);
  const [guestWishes, setGuestWishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Idle');

  const [theme, setTheme] = useState('');
  const [walimatulurus, setWalimatulurus] = useState('');
  const [groomName, setGroomName] = useState('');
  const [brideName, setBrideName] = useState('');
  const [weddingDate, setWeddingDate] = useState('');
  const [H2, setH2] = useState('');
  const [fontP, setFontP] = useState('');

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/kad/${id}`);
        const data = response.data;
        setTheme(data.theme);
        setWalimatulurus(data.walimatulurus);
        setGroomName(data.groom_name);
        setBrideName(data.bride_name);
        setWeddingDate(data.wedding_date);
        setH2(data.H2);
        setFontP(data.fontP);
      } catch (error) {
        console.error('Error fetching invite:', error);
      }
    };

    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/gallery/${id}`);
        const photos = response.data.map(photo => photo.image_url);
        setGalleryImages(photos);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    };

    const fetchWishes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/rsvp/${id}`);
        const filtered = response.data
          .filter(item => item.ucapan !== null)
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .map(item => ({
            name: item.name,
            message: item.ucapan,
            photo: item.image_url,
          }));
        setGuestWishes(filtered);
      } catch (error) {
        console.error('Error fetching wishes:', error);
      }
    };

    fetchInvite();
    fetchGallery();
    fetchWishes();
  }, [id]);

  const proxyUrl = (url) => `https://rsvp.luvvite.com/proxy-image/url?url=${encodeURIComponent(url)}`;

  const cropToSquare = (url) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, size, size);
        resolve(canvas.toDataURL());
      };
      img.src = proxyUrl(url);
    });

  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const generatePages = async () => {
    const pages = [];

    const Components = Themes[theme];
    const weddingDateSet = new Date(weddingDate);
    const weddingDay = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][weddingDateSet.getDay()];
    const formattedDate = weddingDateSet.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    setStatus(t.preparingCoverPage);
    pages.push(
      <div key="cover-page" className={`${Components.themeColor} ${Components.textColor} pdf-page w-[500px] h-[500px] relative flex flex-col justify-center items-center overflow-hidden`} style={{ backgroundImage: `url(${Components.bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className={`${Components.fcPdf}`} style={{
          backgroundImage: `url(${Components.fc})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
          <p className={`text-xs ${fontP} uppercase`}>{walimatulurus}</p>
          <div className={`${Components.centerText}`}>
            <h2 className={`${Components.H2Pdf} ${H2} capitalize`}>{groomName}</h2>
            <p className={`text-2xl ${H2}`}> & </p>
            <h2 className={`${Components.H2Pdf} ${H2} capitalize`}>{brideName}</h2>
          </div>
          <p className={`text-xs ${fontP}`}>{weddingDay}, {formattedDate}</p>
        </div>
        <img src={Components.ft} className={Components.ftPdf} />
        <img src={Components.fb} className={Components.fbPdf} />
      </div>
    );

    pages.push(<div key="photo-title" className="pdf-page w-[500px] h-[500px] flex items-center justify-center text-3xl font-bold">{t.photoMemories}</div>);

    for (const [i, src] of galleryImages.entries()) {
      setStatus(t.croppingGalleryImage.replace('{i}', i + 1).replace('{total}', galleryImages.length));
      setProgress(Math.round(((i + 1) / (galleryImages.length + guestWishes.length / 3 + 3)) * 100));
      const cropped = await cropToSquare(src);
      pages.push(
        <div key={`gallery-${i}`} className="pdf-page w-[500px] h-[500px] flex items-center justify-center p-20 bg-white">
          <img src={cropped} className="max-w-full max-h-full object-contain rounded-lg shadow" />
        </div>
      );
    }

    pages.push(<div key="wish-title" className="pdf-page w-[500px] h-[500px] flex items-center justify-center text-3xl font-bold">{t.guestWishes}</div>);

    const wishChunks = chunkArray(guestWishes, 3);

    for (const [chunkIndex, chunk] of wishChunks.entries()) {
      setStatus(t.processingWishesPage.replace('{chunkIndex}', chunkIndex + 1).replace('{total}', wishChunks.length));
      setProgress(Math.round(((galleryImages.length + chunkIndex + 2) / (galleryImages.length + wishChunks.length + 3)) * 100));
      const croppedImages = await Promise.all(chunk.map(wish => wish.photo ? cropToSquare(wish.photo) : null));
      pages.push(
        <div key={`wish-page-${chunkIndex}`} className="pdf-page w-[500px] h-[500px] bg-white px-20 py-10 flex flex-col justify-evenly">
          {chunk.map((wish, i) => (
            <div key={`wish-${chunkIndex}-${i}`} className={`flex ${i === 1 ? 'flex-row-reverse' : 'flex-row'} items-center gap-4 border p-4 rounded shadow`}>
              {croppedImages[i] && <img src={croppedImages[i]} className="w-20 h-20 object-cover rounded" />}
              <div className="text-xs whitespace-pre-wrap">
                <p className="mb-2">{wish.message}</p>
                <p className="font-bold">– {wish.name}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    setProgress(100);
    setStatus(t.finalizing);
    return pages;
  };

  const renderPaginatedContent = async (pages, filename) => {
    const pdf = new jsPDF('p', 'pt', [794, 794]);
    let added = false;
    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2, useCORS: true,
        windowWidth: pages[i].scrollWidth,
        windowHeight: pages[i].scrollHeight,
      });
      const imgData = canvas.toDataURL('image/png');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (added) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      added = true;
    }
    pdf.save(filename);
  };

  const handleDownload = async () => {
    setLoading(true);
    setProgress(0);
    setStatus('Initializing...');
    const container = document.createElement('div');
    document.body.appendChild(container);
    container.style.position = 'absolute';
    container.style.top = '-9999px';
    container.style.width = '500px';

    const pages = await generatePages();

    const ReactDOM = await import('react-dom/client');
    const root = ReactDOM.createRoot(container);
    root.render(<>{pages}</>);

    setTimeout(async () => {
      await renderPaginatedContent(container.querySelectorAll('.pdf-page'), 'gallery-and-wishes.pdf');
      root.unmount();
      document.body.removeChild(container);
      setStatus(t.done);
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="flex items-center justify-center px-4">
      <div className="bg-white shadow-xl rounded-xl p-8 max-w-md w-full text-center space-y-6">
        <h1 className="text-2xl font-semibold text-gray-800">{t.downloadWeddingMemories}</h1>
        <p className="text-sm text-gray-500">{t.downloadDescription}</p>
        <button
          onClick={handleDownload}
          disabled={loading}
          className={`w-full py-3 rounded-lg font-medium ${loading ? 'bg-gray-400 cursor-not-allowed' : 'btn-primary'}`}
        >
          {loading ? t.preparing : t.downloadPdf}
        </button>

        {loading && (
          <div className="space-y-2">
            <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="text-sm text-gray-600">{status} ({progress}%)</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadInvites;
